import React, { Children } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';
import { motion } from 'framer-motion';
export function Hero() {
  const {
    t
  } = useLanguage();
  const easeOutCubic = [0.33, 1, 0.68, 1];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.18,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.9,
        ease: easeOutCubic
      }
    }
  };
  const scrollToContact = () => {
    document.getElementById('contacto')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToExperience = () => {
    document.getElementById('beneficios')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden" data-id="element-173">
      {/* Subtle grain texture for premium feel */}
      <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" data-id="element-174"></div>

      <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative z-10" data-id="element-175">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-2xl" data-id="element-176">
          {/* Badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-3 mb-8" data-id="element-177">
            <span className="w-8 h-px bg-brand-gold" data-id="element-178"></span>
            <span className="text-brand-ink/60 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-179">
              {t('hero.badge')}
            </span>
            <span className="w-8 h-px bg-brand-gold" data-id="element-180"></span>
          </motion.div>

          {/* Eyebrow */}
          <motion.div variants={itemVariants} className="mb-4" data-id="element-181">
            <span className="text-brand-ink/60 font-medium tracking-[0.25em] text-xs uppercase" data-id="element-182">
              {t('hero.eyebrow')}
            </span>
          </motion.div>

          {/* Emotional headline */}
          <motion.h1 variants={itemVariants} className="text-6xl md:text-7xl lg:text-[5.5rem] font-serif font-light leading-[1.02] text-brand-dark mb-6 tracking-[-0.02em]" data-id="element-183">
            {t('hero.headline')}
          </motion.h1>

          {/* Emotional subline */}
          <motion.p variants={itemVariants} className="font-serif italic text-xl lg:text-2xl text-brand-dark/70 mb-8 leading-snug font-light max-w-xl" data-id="element-184">
            {t('hero.sublineEmotional')}
          </motion.p>

          {/* Procedural sub */}
          <motion.p variants={itemVariants} className="text-base lg:text-lg text-brand-dark/60 mb-12 leading-relaxed font-light max-w-lg" data-id="element-185">
            {t('hero.subheadline')}
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-16" data-id="element-186">
            <Button variant="luxury" size="lg" onClick={scrollToContact} className="w-full sm:w-auto" data-id="element-187">
              {t('hero.cta')}
            </Button>
            <button onClick={scrollToExperience} className="text-brand-dark/60 text-sm font-medium tracking-widest uppercase hover:text-brand-dark transition-colors" data-id="element-188">
              {t('hero.ctaSecondary')} →
            </button>
          </motion.div>

          {/* Trust indicators - refined */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3 text-xs font-medium tracking-widest uppercase text-brand-dark/50" data-id="element-189">
            <span data-id="element-190">{t('hero.trust1')}</span>
            <span className="text-brand-gold" data-id="element-191">·</span>
            <span data-id="element-192">{t('hero.trust2')}</span>
            <span className="text-brand-gold" data-id="element-193">·</span>
            <span data-id="element-194">{t('hero.trust3')}</span>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.4,
        delay: 0.3,
        ease: easeOutCubic
      }} className="relative h-[600px] lg:h-[800px] w-full" data-id="element-195">
          {/* Decorative offset frame */}
          <div className="absolute -top-6 -right-6 lg:-top-8 lg:-right-8 w-full h-full rounded-[2rem] border border-brand-gold/30 -z-10" data-id="element-196"></div>

          <div className="relative h-full w-full rounded-[2rem] overflow-hidden" data-id="element-197">
            <img src="https://cdn.magicpatterns.com/uploads/pkt3ARHa9uXMG2DDfSrpbm/ChatGPT_Image_Jul_6,_2026,_01_11_00_PM.png" alt="Dr. Naveed N. Nosrati, MD — board-certified plastic surgeon at Lipo Laser Los Angeles" className="absolute inset-0 w-full h-full object-cover object-[center_top]" data-id="element-198" />
            {/* Subtle warm overlay to tie into the page palette */}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/20 via-transparent to-transparent pointer-events-none" data-id="element-199"></div>
          </div>

          {/* Floating credentials card — repositioned bottom-left, smaller */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 1.1,
          ease: easeOutCubic
        }} className="absolute -bottom-6 -left-4 lg:-left-8 bg-brand-cream/95 backdrop-blur-md border border-brand-stone/40 rounded-2xl px-6 py-4 z-20 shadow-sm max-w-[260px]" data-id="element-200">
            <div className="flex items-center gap-3 mb-1" data-id="element-201">
              <span className="w-4 h-px bg-brand-gold" data-id="element-202"></span>
              <p className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/50" data-id="element-203">
                {t('hero.doctorCardCredentials')}
              </p>
            </div>
            <p className="font-serif text-base text-brand-dark font-normal italic leading-snug" data-id="element-204">
              {t('hero.doctorCardTagline')}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}